import { useState } from "react";
import { Link2, Copy, Check, Send, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PROPERTY_DOC_TYPES, getRequiredDocs } from "@/hooks/useProperties";

interface CreatePropertyPortalDialogProps {
  propertyId: string;
  propertyTitle: string;
  transactionType: "sale" | "rental";
  clientName?: string | null;
  clientEmail?: string | null;
}

interface PortalInfo {
  id: string;
  token: string;
}

export function CreatePropertyPortalDialog({
  propertyId,
  propertyTitle,
  transactionType,
  clientName,
  clientEmail,
}: CreatePropertyPortalDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [portal, setPortal] = useState<PortalInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(clientName || "");
  const [email, setEmail] = useState(clientEmail || "");

  const portalUrl = portal
    ? `${window.location.origin}/immo-portal/${portal.token}`
    : "";

  const handleCreate = async () => {
    if (!user?.id || !name.trim()) return;

    setLoading(true);
    try {
      // Create portal
      const { data: portalData, error: portalError } = await supabase
        .from("property_portals")
        .insert({
          property_id: propertyId,
          user_id: user.id,
          client_name: name.trim(),
          client_email: email.trim() || null,
        })
        .select("id, token")
        .single();

      if (portalError) throw portalError;

      // Create portal items based on required documents
      const requiredDocs = getRequiredDocs(transactionType);
      const items = requiredDocs.map((docType, index) => ({
        portal_id: portalData.id,
        doc_type: docType,
        label: PROPERTY_DOC_TYPES[docType]?.label || docType,
        is_required: true,
        sort_order: index,
      }));

      if (items.length > 0) {
        const { error: itemsError } = await supabase
          .from("property_portal_items")
          .insert(items);

        if (itemsError) throw itemsError;
      }

      setPortal(portalData);
      toast.success("Portail client créé !");
    } catch (error) {
      console.error("Error creating portal:", error);
      toast.error("Erreur lors de la création du portail");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Erreur lors de la copie");
    }
  };

  const handleEmailSend = () => {
    const subject = encodeURIComponent(`Documents requis - ${propertyTitle}`);
    const body = encodeURIComponent(
      `Bonjour ${name},\n\nVeuillez déposer vos documents via ce lien sécurisé :\n${portalUrl}\n\nCordialement`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Reset state when closing
      setPortal(null);
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Link2 className="mr-2 h-4 w-4" />
          Créer portail client
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un portail client</DialogTitle>
          <DialogDescription>
            Générez un lien pour que votre client puisse déposer ses documents.
          </DialogDescription>
        </DialogHeader>

        {!portal ? (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client-name">Nom du client *</Label>
              <Input
                id="client-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="M. et Mme Dupont"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-email">Email (optionnel)</Label>
              <Input
                id="client-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="client@email.com"
              />
            </div>

            <Button
              onClick={handleCreate}
              disabled={loading || !name.trim()}
              className="w-full"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Générer le lien
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Input
                readOnly
                value={portalUrl}
                className="text-xs bg-transparent border-0 focus-visible:ring-0"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex gap-2">
              {email && (
                <Button
                  variant="outline"
                  onClick={handleEmailSend}
                  className="flex-1"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Envoyer par email
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => window.open(portalUrl, "_blank")}
                className="flex-1"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Prévisualiser
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Ce lien est unique et sécurisé. Le client pourra y déposer les
              documents requis.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
